import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  CalendarMonthResponseSchema,
  FeedUrlResponseSchema,
  type CalendarMonthResponse,
  type FeedUrlResponse,
} from "@/api/types/calendar";

/** Query-key factory — mirrors the events hook convention. */
export const calendarKeys = {
  all: ["calendar"] as const,
  month: (year: number, month: number) => [...calendarKeys.all, "month", year, month] as const,
};

/**
 * Builder 4's own month view (app/api/v2/calendar.py::GET /calendar/month
 * — year+month, per-day events + ACTIVE holds), distinct from the existing
 * GET /v2/events/calendar (useEventsCalendar in useEvents.ts), which has
 * no hold awareness.
 */
export function useCalendarMonth(year: number, month: number) {
  return useQuery<CalendarMonthResponse>({
    queryKey: calendarKeys.month(year, month),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/calendar/month?year=${year}&month=${month}`);
      return validateResponse(CalendarMonthResponseSchema, data, "GET /v2/calendar/month");
    },
    staleTime: 15 * 1000,
  });
}

/**
 * Mints a fresh ICS feed token + URL (app/api/v2/calendar.py::GET
 * /calendar/feed-url). Modeled as a mutation, not a query, even though
 * it's a GET under the hood — it's a "generate on demand" action (each
 * call mints an equally-valid but different-looking token) triggered by a
 * button click, not data the page should auto-fetch on mount.
 */
export function useCalendarFeedUrl() {
  return useMutation<FeedUrlResponse, unknown, void>({
    mutationFn: async () => {
      const { data } = await apiClient.get("/v2/calendar/feed-url");
      return validateResponse(FeedUrlResponseSchema, data, "GET /v2/calendar/feed-url");
    },
  });
}
